import {DataTable} from "../DataTable/DataTable";
import {Quantity, TransportAssocEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {ActionIcon, Anchor, Group, Text} from "@mantine/core";
import {Link, useNavigate} from "react-router-dom";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {ServiceTypeCell} from "./cells/ServiceTypeCell/ServiceTypeCell";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {useTicketDataTableFilter} from "./useTicketDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {DataTableSumFooter} from "../DataTable/footer/DataTableSumFooter/DataTableSumFooter";
import {IconFlag, IconFlagFilled, IconMap, IconReportMoney} from "@tabler/icons-react";
import {Tooltip} from "../../tooltip/Tooltip/Tooltip";
import {ReactNode, useCallback, useState} from "react";
import {useCostedDataTable} from "../DataTable/useCostedDataTable";
import {CostHeader} from "../DataTable/header/CostHeader/CostHeader";
import {FormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency";
import {CostDetailsModal} from "../../modal/CostDetailsModal/CostDetailsModal";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

export type TicketDataTableProps =
  EntityDataTableProps<TransportAssocEntity>
  & {
  ticketUrlPrefix?: string
}

const EMPTY_ARRAY: TransportAssocEntity[] = []

export const TicketDataTable = (props: TicketDataTableProps) => {
  const {
    records: tickets = EMPTY_ARRAY,
    recordsPerPageOptions,
    ticketUrlPrefix,
    ...rest
  } = props;

  const [selectedRecord, setSelectedRecord] = useState<TransportAssocEntity | undefined>(undefined)

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const costedTableProps = useCostedDataTable({
    records: tickets,
  })

  const {filteredRecords, filterConfig} = useTicketDataTableFilter({
    records: tickets
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'startDttm', direction: 'asc'}
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const onViewMap = useCallback((record: TransportAssocEntity) => {
    let url = `${(ticketUrlPrefix ?? "")}/tickets/profile/map?id=${record.id}`
    if (record.database) {
      url += `&database=${record.database}`
    }
    navigate(url)
  }, [navigate, ticketUrlPrefix]);

  return (
    <>
      <DataTable
        {...rest}
        {...(pagination ?? {})}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        rowContextMenu={{
          items: (record) => [
            {
              key: 'view_map',
              title: "View Map",
              icon: <IconMap size={14}/>,
              onClick: () => onViewMap(record),
            },
          ],
        }}
        columns={[
          {
            title: '',
            accessor: "warnings",
            width: 40,
            render: ({warnings}) => {
              const warning = warnings?.find(w => w.type === 'TTA_DELIVERY_TO_NON_PRIMARY_SITE')
              if (warning) {
                let color = "warning-low"
                let icon: ReactNode = <IconFlag size={14}/>

                if (warning.priority === 'MEDIUM') {
                  color = "warning-medium"
                  icon = <IconFlag size={14}/>
                } else if (warning.priority === "HIGH") {
                  color = "warning-high"
                  icon = <IconFlagFilled size={14}/>
                }

                return (
                  <Text c={color}>
                    <Tooltip label={warning.message}>
                      {icon}
                    </Tooltip>
                  </Text>
                )
              }

              return undefined
            },
          },
          {
            ...getColumnFilterProps(filterConfig, "id"),
            title: t("table.entity.ticket.column.id", "ID"),
            accessor: "resourceId",
            width: 100,
            render: ({resourceIdentifiers, id, database}) => {
              let url = `${(ticketUrlPrefix ?? "")}/tickets/profile?id=${id}`
              if (database) {
                url += `&database=${database}`
              }

              return (
                <Anchor component={Link} to={url}>
                  <Group spacing="xs">
                    <DefaultText value={resourceIdentifiers.join()}/>
                  </Group>
                </Anchor>
              )
            },
          },
          {
            title: t("table.entity.ticket.column.starttime", "Start Time"),
            accessor: "startDttm",
            width: 100,
            render: (tta) => (
              <FormattedDateTime date={tta.startDttm}/>
            ),
          },
          {
            title: t("table.entity.ticket.column.endtime", "End Time"),
            accessor: "endDttm",
            width: 100,
            render: (tta) => (
              <FormattedDateTime date={tta.endDttm}/>
            ),
          },
          {
            ...getColumnFilterProps(filterConfig, "pickup"),
            title: t("table.entity.ticket.column.pickupsite", "P/U Site"),
            accessor: "pickupTransport.siteName",
            width: 100,
            sortable: true,
            render: (tta) => (
              <DefaultText value={tta.pickupTransport?.siteName}/>
            ),
          },
          {
            title: t("table.entity.ticket.column.pickupquantity", "P/U Qty"),
            accessor: "pickupTransport.quantityDecimal",
            width: 100,
            sortable: true,
            render: (tta) => {
              const qty: Partial<Quantity> = {
                number: tta.pickupTransport?.quantityDecimal,
                units: tta.pickupTransport?.quantityUnitOfMeasure
              }

              return <FormattedQuantity value={qty}/>
            },
            footer: (
              <DataTableSumFooter
                value={{
                  number: filteredRecords.reduce((prev, curr) => {
                    const qty = curr.pickupTransport?.quantityDecimal ?? 0
                    return prev + qty
                  }, 0)
                }}
              />
            ),
          },
          {
            ...getColumnFilterProps(filterConfig, "delivery"),
            title: t("table.entity.ticket.column.deliverysite", "Del. Site"),
            accessor: "deliveryTransport.siteName",
            width: 100,
            sortable: true,
            render: (tta) => (
              <DefaultText value={tta.deliveryTransport?.siteName}/>
            ),
          },
          {
            title: t("table.entity.ticket.column.delivertyquantity", "Del. Qty"),
            accessor: "deliveryTransport.quantityDecimal",
            width: 100,
            sortable: true,
            render: (tta) => {
              const qty: Partial<Quantity> = {
                number: tta.deliveryTransport?.quantityDecimal,
                units: tta.deliveryTransport?.quantityUnitOfMeasure
              }

              return <FormattedQuantity value={qty}/>
            },
            footer: (
              <DataTableSumFooter
                value={{
                  number: filteredRecords.reduce((prev, curr) => {
                    const qty = curr.deliveryTransport?.quantityDecimal ?? 0
                    return prev + qty
                  }, 0)
                }}
              />
            ),
          },
          {
            ...getColumnFilterProps(filterConfig, "freightType"),
            title: t("table.entity.ticket.column.freighttype", "Freight Type"),
            accessor: "pickupTransport.waterTypeName",
            width: 100,
            sortable: true,
            render: (tta) => (
              <DefaultText value={tta.pickupTransport?.waterTypeName}/>
            )
          },
          {
            ...getColumnFilterProps(filterConfig, "serviceType"),
            title: t("table.entity.ticket.column.servicetype", "Work Type"),
            accessor: "pickupTransport.serviceTypeName",
            width: 100,
            sortable: true,
            render: (tta) => (
              <ServiceTypeCell tta={tta}/>
            )
          },
          {
            ...getColumnFilterProps(filterConfig, "truckCompany"),
            title: t("table.entity.ticket.column.truckcompany", "Truck Company"),
            accessor: "pickupTransport.parentCompanyName",
            width: 100,
            sortable: true,
            render: (tta) => (
              <DefaultText
                value={tta.pickupTransport?.trailerParentCompanyName ?? tta.pickupTransport?.trailerCompanyName}/>
            )
          },
          {
            title: (
              <CostHeader
                {...costedTableProps}
                defaultTitle={t("table.entity.ticket.column.cost", "Cost")}/>
            ),
            accessor: "totalCost",
            width: 125,
            sortable: true,
            render: (record) => {
              return (
                <Group spacing={4} position="right" noWrap>
                  <FormattedCurrency value={record.getTotalCost(costedTableProps.selectedRateModel)}/>
                  <ActionIcon variant="transparent" onClick={() => setSelectedRecord(record)}>
                    <IconReportMoney size={14}/>
                  </ActionIcon>
                </Group>
              )
            },
            footer: (
              <DataTableSumFooter
                value={{
                  number: filteredRecords.reduce((prev, curr) => prev + (curr.getTotalCost(costedTableProps.selectedRateModel) ?? 0), 0)
                }}
              />
            ),
          },
        ]}
      />
      {selectedRecord &&
        <CostDetailsModal
          title={"Cost Breakdown"}
          size="xl"
          opened={true}
          onClose={() => setSelectedRecord(undefined)}
          value={selectedRecord}/>
      }
    </>
  );
};
